var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useApiClient } from "@/context/ApiClientContext";
import europeanCountries from "@/data/europeanCountries.json";
import "./ProfilePage.css";
import { validateEmailFormat, validatePassword, } from "../../utils/loginValidation";
import ValidatedInput from "../../components/profile/ValidatedInput";
import { validateField } from "../../utils/registerValitation";
import { validateAddress } from "../../utils/editValidation";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
const fieldNameMap = {
    streetName: "shippingStreet",
    city: "shippingCity",
    postalCode: "shippingPostalCode",
    country: "shippingCountry",
    state: "shippingCity",
    id: "email",
};
function isAddressEqual(a, b) {
    return (a.streetName === b.streetName &&
        a.postalCode === b.postalCode &&
        a.city === b.city &&
        a.country === b.country &&
        a.state === b.state);
}
const ProfilePage = () => {
    const { customer, setCustomer, relogin } = useAuth();
    const apiClient = useApiClient();
    const [isEditing, setIsEditing] = useState(false);
    // Personal information state
    const [editedFirstName, setEditedFirstName] = useState("");
    const [editedLastName, setEditedLastName] = useState("");
    const [editedDOB, setEditedDOB] = useState("");
    const [editedEmail, setEditedEmail] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [missingAddressType, setMissingAddressType] = useState(null);
    const [canAddNewAddress, setCanAddNewAddress] = useState(true);
    const [showNewAddressForm, setShowNewAddressForm] = useState(false);
    const [touchedAddressFields, setTouchedAddressFields] = useState({});
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [currentPasswordError, setCurrentPasswordError] = useState("");
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    // Field errors state
    const [fieldErrors, setFieldErrors] = useState({
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        email: "",
    });
    useEffect(() => {
        setPasswordError(validatePassword(newPassword));
    }, [newPassword]);
    useEffect(() => {
        setFieldErrors({
            firstName: validateField("firstName", editedFirstName, formData, europeanCountries, false),
            lastName: validateField("lastName", editedLastName, formData, europeanCountries, false),
            dateOfBirth: validateField("dateOfBirth", editedDOB, formData, europeanCountries, false),
            email: validateField("email", editedEmail, formData, europeanCountries, false),
        });
    }, [editedFirstName, editedLastName, editedDOB, editedEmail]);
    const resetPasswordState = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setPasswordError("");
        setErrorMessage("");
        setCurrentPasswordError("");
    };
    const [newAddress, setNewAddress] = useState({
        id: "",
        streetName: "",
        postalCode: "",
        city: "",
        state: "",
        country: "",
    });
    const formData = {
        email: editedEmail,
        password: "",
        confirmPassword: "",
        firstName: editedFirstName,
        lastName: editedLastName,
        dateOfBirth: editedDOB,
        shippingCountry: newAddress.country,
        shippingCity: newAddress.city,
        shippingStreet: newAddress.streetName,
        shippingPostalCode: newAddress.postalCode,
        billingCountry: "",
        billingCity: "",
        billingStreet: "",
        billingPostalCode: "",
    };
    const newAddressFormData = {
        email: editedEmail,
        password: "",
        confirmPassword: "",
        firstName: editedFirstName,
        lastName: editedLastName,
        dateOfBirth: editedDOB,
        shippingCountry: newAddress.country,
        shippingCity: newAddress.city,
        shippingStreet: newAddress.streetName,
        shippingPostalCode: newAddress.postalCode,
        billingCountry: "",
        billingCity: "",
        billingStreet: "",
        billingPostalCode: "",
    };
    const newAddressErrors = {
        streetName: !newAddress.streetName.trim()
            ? "Street address is required"
            : "",
        city: !newAddress.city.trim() ? "City is required" : "",
        country: !newAddress.country.trim() ? "Country is required" : "",
        postalCode: validateField("shippingPostalCode", newAddress.postalCode, newAddressFormData, europeanCountries, false),
    };
    const isNewAddressSaveDisabled = Object.values(newAddressErrors).some(Boolean);
    // console.log("apiClient.customerApiRoot", apiClient["customerApiRoot"]);
    if (!customer || !apiClient) {
        return _jsx("p", { children: "Loading..." });
    }
    const { firstName, lastName, dateOfBirth, addresses, defaultBillingAddressId, defaultShippingAddressId, version, email, } = customer;
    useEffect(() => {
        const shippingSet = !!defaultShippingAddressId;
        const billingSet = !!defaultBillingAddressId;
        const isSame = defaultShippingAddressId === defaultBillingAddressId;
        setCanAddNewAddress(!isSame && shippingSet !== billingSet);
        if (!shippingSet)
            setMissingAddressType("shipping");
        else if (!billingSet)
            setMissingAddressType("billing");
        else
            setMissingAddressType(null);
    }, [defaultShippingAddressId, defaultBillingAddressId]);
    const isChanged = editedFirstName !== firstName ||
        editedLastName !== lastName ||
        editedDOB !== dateOfBirth ||
        editedEmail !== email;
    const passwordMismatch = (newPassword || confirmNewPassword || currentPassword) &&
        newPassword !== confirmNewPassword;
    const isPersonalSaveDisabled = Object.values(fieldErrors).some(Boolean) || !isChanged || passwordMismatch;
    const emailError = validateField("email", editedEmail, {
        email: editedEmail,
        password: "",
        confirmPassword: "",
        firstName: editedFirstName,
        lastName: editedLastName,
        dateOfBirth: editedDOB,
        shippingCountry: "",
        shippingCity: "",
        shippingStreet: "",
        shippingPostalCode: "",
        billingCountry: "",
        billingCity: "",
        billingStreet: "",
        billingPostalCode: "",
    }, europeanCountries, false);
    const normalizeAddresses = (addresses) => addresses.map((addr) => {
        var _a, _b, _c, _d, _e;
        return ({
            id: addr.id,
            streetName: (_a = addr.streetName) !== null && _a !== void 0 ? _a : "",
            postalCode: (_b = addr.postalCode) !== null && _b !== void 0 ? _b : "",
            city: (_c = addr.city) !== null && _c !== void 0 ? _c : "",
            country: (_d = addr.country) !== null && _d !== void 0 ? _d : "",
            state: (_e = addr.state) !== null && _e !== void 0 ? _e : "",
        });
    });
    const [editedAddresses, setEditedAddresses] = useState(normalizeAddresses(customer.addresses));
    const originalAddresses = normalizeAddresses(customer.addresses);
    const [editingAddressIndex, setEditingAddressIndex] = useState(null);
    const [addressErrors, setAddressErrors] = useState({});
    const handleAddressChange = (index, field, value) => {
        const updatedAddresses = [...editedAddresses];
        const updatedAddress = Object.assign(Object.assign({}, updatedAddresses[index]), { [field]: value });
        if (field === "country") {
            updatedAddress.postalCode = "";
        }
        updatedAddresses[index] = updatedAddress;
        setEditedAddresses(updatedAddresses);
        setTouchedAddressFields((prev) => {
            const current = new Set(prev[index] || []);
            current.add(field);
            return Object.assign(Object.assign({}, prev), { [index]: current });
        });
        const formData = {
            email: editedEmail,
            password: "",
            confirmPassword: "",
            firstName: editedFirstName,
            lastName: editedLastName,
            dateOfBirth: editedDOB,
            shippingCountry: updatedAddress.country,
            shippingCity: updatedAddress.city,
            shippingStreet: updatedAddress.streetName,
            shippingPostalCode: updatedAddress.postalCode,
            billingCountry: "",
            billingCity: "",
            billingStreet: "",
            billingPostalCode: "",
        };
        const fieldKey = fieldNameMap[field];
        if (!fieldKey)
            return;
        const error = validateField(fieldKey, value, formData, europeanCountries, false);
        setAddressErrors((prev) => (Object.assign(Object.assign({}, prev), { [index]: Object.assign(Object.assign({}, (prev[index] || {})), { [field]: error || "" }) })));
    };
    const isAddressSaveDisabled = (index) => {
        const errors = addressErrors[index];
        const address = editedAddresses[index];
        const originalAddress = originalAddresses[index];
        const notChanged = isAddressEqual(address, originalAddress);
        return (!address.streetName ||
            !address.city ||
            !address.country ||
            !address.postalCode ||
            Object.values(errors || {}).some(Boolean) ||
            notChanged);
    };
    const startEdit = () => {
        setEditedFirstName(firstName);
        setEditedLastName(lastName);
        setEditedDOB(dateOfBirth);
        setEditedEmail(email);
        setErrorMessage("");
        setIsEditing(true);
    };
    const cancelEdit = () => {
        setIsEditing(false);
    };
    const startAddressEdit = (index) => {
        setEditingAddressIndex(index);
        setAddressErrors((prev) => (Object.assign(Object.assign({}, prev), { [index]: {} })));
        setTouchedAddressFields((prev) => (Object.assign(Object.assign({}, prev), { [index]: new Set() })));
    };
    const cancelAddressEdit = () => {
        setEditingAddressIndex(null);
        setEditedAddresses(normalizeAddresses(customer.addresses));
        setAddressErrors((prev) => (Object.assign(Object.assign({}, prev), { [editingAddressIndex]: {} })));
        setTouchedAddressFields((prev) => (Object.assign(Object.assign({}, prev), { [editingAddressIndex]: new Set() })));
    };
    const saveAddressChanges = (index) => __awaiter(void 0, void 0, void 0, function* () {
        const address = editedAddresses[index];
        if (!address.id)
            return;
        const errors = validateAddress(address);
        if (Object.keys(errors).length > 0) {
            setAddressErrors((prev) => (Object.assign(Object.assign({}, prev), { [index]: Object.assign(Object.assign({}, (prev[index] || {})), errors) })));
            return;
        }
        setAddressErrors((prev) => (Object.assign(Object.assign({}, prev), { [index]: {} })));
        try {
            const updated = yield apiClient.updateCustomer({
                version: customer.version,
                actions: [
                    {
                        action: "changeAddress",
                        addressId: address.id,
                        address: {
                            streetName: address.streetName,
                            postalCode: address.postalCode,
                            city: address.city,
                            state: address.state,
                            country: address.country,
                        },
                    },
                ],
            });
            setCustomer(updated);
            setEditingAddressIndex(null);
            toast.success("Address updated successfully!");
        }
        catch (error) {
            console.error("Failed to update address", error);
            toast.error("Failed to update address.");
        }
    });
    const saveChanges = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!editedFirstName || !editedLastName || !editedDOB) {
            setErrorMessage("All fields are required.");
            return;
        }
        if (editedEmail && !validateEmailFormat(editedEmail)) {
            setErrorMessage("Invalid email address format.");
            return;
        }
        const actions = [
            { action: "setFirstName", firstName: editedFirstName },
            { action: "setLastName", lastName: editedLastName },
            { action: "setDateOfBirth", dateOfBirth: editedDOB },
        ];
        const wantsToChangePassword = currentPassword || newPassword || confirmNewPassword;
        if (editedEmail !== email) {
            actions.push({ action: "changeEmail", email: editedEmail });
        }
        if (wantsToChangePassword) {
            if (!currentPassword || !newPassword || !confirmNewPassword) {
                setErrorMessage("Please fill in all password fields.");
                return;
            }
            if (newPassword !== confirmNewPassword) {
                setErrorMessage("New passwords do not match.");
                return;
            }
        }
        try {
            yield apiClient.updateCustomer({
                version,
                actions,
            });
            const updatedCustomer = yield apiClient.getCustomerProfile();
            setCustomer(updatedCustomer);
            toast.success("Profile information updated!");
            if (wantsToChangePassword) {
                yield apiClient.changePassword(currentPassword, newPassword, updatedCustomer.version);
                const refreshedCustomer = yield apiClient.getCustomerProfile();
                setCustomer(refreshedCustomer);
            }
            setIsEditing(false);
            resetPasswordState();
        }
        catch (error) {
            toast.error("Failed to update profile.");
            let msg = "Failed to update profile. Please try again.";
            if (typeof error === "object" &&
                error !== null &&
                "message" in error &&
                typeof error.message === "string") {
                const errorMessage = error.message;
                if (errorMessage.includes("current password") ||
                    errorMessage.includes("InvalidCurrentPassword")) {
                    msg = "Incorrect current password.";
                }
                else {
                    msg = errorMessage;
                }
            }
            setErrorMessage(msg);
        }
    });
    const setDefaultAddress = (addressId, type) => __awaiter(void 0, void 0, void 0, function* () {
        const isCurrentlySet = type === "shipping"
            ? defaultShippingAddressId === addressId
            : defaultBillingAddressId === addressId;
        const action = type === "shipping"
            ? "setDefaultShippingAddress"
            : "setDefaultBillingAddress";
        const payload = {
            action,
            addressId: isCurrentlySet ? null : addressId,
        };
        try {
            const updated = yield apiClient.updateCustomer({
                version: customer.version,
                actions: [payload],
            });
            setCustomer(updated);
            const shippingSet = !!updated.defaultShippingAddressId;
            const billingSet = !!updated.defaultBillingAddressId;
            const isSame = updated.defaultShippingAddressId === updated.defaultBillingAddressId;
            setCanAddNewAddress(!isSame && shippingSet !== billingSet);
            if (!shippingSet)
                setMissingAddressType("shipping");
            else if (!billingSet)
                setMissingAddressType("billing");
            else
                setMissingAddressType(null);
        }
        catch (error) {
            console.error("Failed to update default address", error);
        }
    });
    const handleAddNewAddress = () => __awaiter(void 0, void 0, void 0, function* () {
        if (isNewAddressSaveDisabled)
            return;
        try {
            const updated = yield apiClient.updateCustomer({
                version: customer.version,
                actions: [
                    {
                        action: "addAddress",
                        address: {
                            streetName: newAddress.streetName,
                            city: newAddress.city,
                            country: newAddress.country,
                            postalCode: newAddress.postalCode,
                        },
                    },
                ],
            });
            setCustomer(updated);
            setNewAddress({
                id: "",
                streetName: "",
                postalCode: "",
                city: "",
                state: "",
                country: "",
            });
            setShowNewAddressForm(false);
        }
        catch (error) {
            console.error("Failed to add new address", error);
        }
    });
    const handlePasswordChange = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            yield apiClient.changePassword(currentPassword, newPassword, customer.version);
            localStorage.removeItem("accessToken");
            yield relogin({ email, password: newPassword });
            const updatedCustomer = yield apiClient.getCustomerProfile();
            setCustomer(updatedCustomer);
            setShowPasswordForm(false);
            resetPasswordState();
            //show success message
            toast.success("Password was successfully changed!");
        }
        catch (err) {
            if (typeof err === "object" &&
                err !== null &&
                "body" in err &&
                typeof err.body === "object" &&
                ((_a = err.body) === null || _a === void 0 ? void 0 : _a.errors) &&
                Array.isArray(err.body.errors)) {
                const errorList = err.body
                    .errors;
                const isInvalidPassword = errorList.some((e) => e.code === "InvalidCurrentPassword");
                if (isInvalidPassword) {
                    setCurrentPasswordError("Current password is incorrect");
                    return;
                }
            }
            toast.error("Password change failed. Please try again later.");
        }
    });
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "profile-page", children: [_jsx("h2", { children: "User Profile" }), _jsxs("section", { className: "personal-info", children: [_jsx("h3", { children: "Personal Information" }), errorMessage && _jsx("p", { className: "error-message", children: errorMessage }), isEditing ? (_jsxs("div", { className: "edit-form-container", children: [_jsx(ValidatedInput, { label: "First Name", type: "text", value: editedFirstName, placeholder: "First Name", error: fieldErrors.firstName ||
                                        (!editedFirstName.trim()
                                            ? "First name is required"
                                            : undefined), onChange: setEditedFirstName }), _jsx(ValidatedInput, { label: "Last Name", type: "text", value: editedLastName, placeholder: "Last Name", error: fieldErrors.lastName ||
                                        (!editedLastName.trim() ? "Last name is required" : undefined), onChange: setEditedLastName }), _jsx(ValidatedInput, { label: "Date of Birth", type: "date", value: editedDOB, placeholder: "Date of Birth", error: fieldErrors.dateOfBirth ||
                                        (!editedDOB ? "Date of birth is required" : undefined), onChange: setEditedDOB }), _jsx(ValidatedInput, { label: "Email Address", type: "text", value: editedEmail, placeholder: "Email Address", error: emailError ||
                                        (!editedEmail.trim() ? "Email is required" : undefined), onChange: setEditedEmail }), _jsxs("div", { className: "edit-buttons-container", children: [_jsx("button", { onClick: saveChanges, className: `save-button ${isPersonalSaveDisabled ? "disabled" : ""}`, disabled: isPersonalSaveDisabled, children: "Save" }), _jsx("button", { onClick: cancelEdit, className: "close-button", children: "Cancel" })] })] })) : (_jsxs(_Fragment, { children: [_jsxs("p", { className: "p-text", children: [_jsx("strong", { children: "First Name:" }), " ", firstName] }), _jsxs("p", { className: "p-text", children: [_jsx("strong", { children: "Last Name:" }), " ", lastName] }), _jsxs("p", { className: "p-text", children: [_jsx("strong", { children: "Date of Birth:" }), " ", dateOfBirth] }), _jsxs("p", { className: "p-text", children: [_jsx("strong", { children: "Your email address:" }), " ", email] }), _jsxs("div", { className: "edit-buttons-row", children: [_jsx("button", { onClick: startEdit, className: "edit-button", children: "Edit" }), _jsx("button", { onClick: () => setShowPasswordForm((prev) => !prev), className: "edit-button", children: showPasswordForm
                                                ? "Cancel Password Change"
                                                : "Change Password" })] })] })), showPasswordForm && (_jsxs(_Fragment, { children: [_jsx("h4", { className: "password-change-title", children: "Change Password" }), _jsxs("div", { className: "edit-form-container", children: [_jsxs("div", { className: "password-input-wrapper", children: [_jsx("p", { className: "p-text-password", children: "Your current password:" }), _jsx("input", { type: showCurrentPassword ? "text" : "password", value: currentPassword, onChange: (e) => {
                                                        setCurrentPassword(e.target.value);
                                                        setCurrentPasswordError("");
                                                    }, placeholder: "Current Password", className: "edit-input" }), _jsx("span", { onClick: () => setShowCurrentPassword((prev) => !prev), className: "eye-icon", children: showCurrentPassword ? _jsx(FaEyeSlash, {}) : _jsx(FaEye, {}) })] }), !currentPassword && (_jsx("p", { className: "error-message", children: "Current password is required" })), currentPasswordError && (_jsx("p", { className: "error-message", children: currentPasswordError })), _jsxs("div", { className: "password-input-wrapper", children: [_jsx("p", { className: "p-text-password", children: "Your new password:" }), _jsx("input", { type: showNewPassword ? "text" : "password", value: newPassword, onChange: (e) => setNewPassword(e.target.value), placeholder: "New Password", className: "edit-input" }), _jsx("span", { onClick: () => setShowNewPassword((prev) => !prev), className: "eye-icon", children: showNewPassword ? _jsx(FaEyeSlash, {}) : _jsx(FaEye, {}) })] }), passwordError && (_jsx("p", { className: "error-message", children: passwordError })), _jsxs("div", { className: "password-input-wrapper", children: [_jsx("p", { className: "p-text-password", children: "Confirm your new password:" }), _jsx("input", { type: showConfirmPassword ? "text" : "password", value: confirmNewPassword, onChange: (e) => setConfirmNewPassword(e.target.value), placeholder: "Confirm New Password", className: "edit-input" }), _jsx("span", { onClick: () => setShowConfirmPassword((prev) => !prev), className: "eye-icon", children: showConfirmPassword ? _jsx(FaEyeSlash, {}) : _jsx(FaEye, {}) })] }), !confirmNewPassword && (_jsx("p", { className: "error-message", children: "Confirm new password is required" })), newPassword &&
                                            confirmNewPassword &&
                                            newPassword !== confirmNewPassword && (_jsx("p", { className: "error-message", children: "New passwords do not match" })), _jsxs("div", { className: "edit-buttons-container", children: [_jsx("button", { type: "button", className: "save-button", disabled: !currentPassword ||
                                                        !newPassword ||
                                                        !confirmNewPassword ||
                                                        newPassword !== confirmNewPassword ||
                                                        !!passwordError, onClick: handlePasswordChange, children: "Save Password" }), _jsx("button", { className: "close-button", onClick: () => {
                                                        setShowPasswordForm(false);
                                                        resetPasswordState();
                                                    }, children: "Cancel" })] })] })] }))] }), _jsxs("section", { className: "addresses", children: [_jsx("h3", { className: "h3-addresses", children: "Delivery addresses:" }), addresses && addresses.length > 0 ? (addresses.map((addr, index) => {
                            var _a, _b, _c, _d, _e, _f, _g, _h;
                            return (_jsx("div", { className: "address-card", children: editingAddressIndex === index ? (_jsxs("div", { className: "address-edit-form", children: [_jsxs("h4", { className: "address-title", children: ["Editing Address ", index + 1] }), _jsx("p", { className: "p-text-address", children: "Your full adress:" }), _jsx("input", { type: "text", value: editedAddresses[index].streetName, onChange: (e) => handleAddressChange(index, "streetName", e.target.value), placeholder: "Street Name", className: "edit-input" }), ((_a = touchedAddressFields[index]) === null || _a === void 0 ? void 0 : _a.has("streetName")) &&
                                            ((_b = addressErrors[index]) === null || _b === void 0 ? void 0 : _b.streetName) && (_jsx("p", { className: "error-message", children: addressErrors[index].streetName })), _jsx("p", { className: "p-text-address", children: "Postal code:" }), _jsx("input", { type: "text", value: editedAddresses[index].postalCode, onChange: (e) => handleAddressChange(index, "postalCode", e.target.value), placeholder: "Postal Code", className: "edit-input" }), ((_c = touchedAddressFields[index]) === null || _c === void 0 ? void 0 : _c.has("postalCode")) &&
                                            ((_d = addressErrors[index]) === null || _d === void 0 ? void 0 : _d.postalCode) && (_jsx("p", { className: "error-message", children: addressErrors[index].postalCode })), _jsx("p", { className: "p-text-address", children: "Your city:" }), _jsx("input", { type: "text", value: editedAddresses[index].city, onChange: (e) => handleAddressChange(index, "city", e.target.value), placeholder: "City", className: "edit-input" }), ((_e = touchedAddressFields[index]) === null || _e === void 0 ? void 0 : _e.has("city")) &&
                                            ((_f = addressErrors[index]) === null || _f === void 0 ? void 0 : _f.city) && (_jsx("p", { className: "error-message", children: addressErrors[index].city })), _jsx("p", { className: "p-text-address", children: "Select your country:" }), _jsxs("select", { value: editedAddresses[index].country, onChange: (e) => handleAddressChange(index, "country", e.target.value), className: "edit-input", children: [_jsx("option", { value: "", children: "Select a country" }), europeanCountries.map(({ code, name }) => (_jsx("option", { value: code, children: name }, code)))] }), ((_g = touchedAddressFields[index]) === null || _g === void 0 ? void 0 : _g.has("country")) &&
                                            ((_h = addressErrors[index]) === null || _h === void 0 ? void 0 : _h.country) && (_jsx("p", { className: "error-message", children: addressErrors[index].country })), _jsxs("div", { className: "edit-button-row", children: [_jsx("button", { onClick: () => saveAddressChanges(index), className: "save-button", disabled: isAddressSaveDisabled(index), children: "Save" }), _jsx("button", { onClick: cancelAddressEdit, className: "close-button", children: "Cancel" })] })] })) : (_jsxs(_Fragment, { children: [_jsxs("h4", { className: "address-title", children: ["Address ", index + 1] }), _jsxs("p", { className: "p-text", children: [addr.streetName, ", ", addr.postalCode, ", ", addr.city, ",", " ", addr.country] }), _jsxs("div", { className: "label-container", children: [_jsxs("label", { className: `checkbox-toggle ${addr.id === defaultShippingAddressId ? "active" : ""}`, children: [_jsx("input", { type: "checkbox", checked: addr.id === defaultShippingAddressId, onChange: () => setDefaultAddress(addr.id, "shipping") }), "Default Shipping address"] }), _jsxs("label", { className: `checkbox-toggle ${addr.id === defaultBillingAddressId ? "active" : ""}`, children: [_jsx("input", { type: "checkbox", checked: addr.id === defaultBillingAddressId, onChange: () => setDefaultAddress(addr.id, "billing") }), "Default Billing address"] })] }), _jsx("p", { className: `address-label ${addr.id !== defaultBillingAddressId
                                                ? "not-default"
                                                : "default"}`, children: addr.id === defaultBillingAddressId
                                                ? "🏷️ Default Billing Address"
                                                : "❗ This address is not Default Billing Address" }), _jsx("p", { className: `address-label ${addr.id !== defaultShippingAddressId
                                                ? "not-default"
                                                : "default"}`, children: addr.id === defaultShippingAddressId
                                                ? "📦 Default Shipping Address"
                                                : "❗ This address is not Default Shipping Address" }), _jsx("div", { className: "button-container", children: _jsx("button", { onClick: () => startAddressEdit(index), className: "edit-button-address", children: "Edit Address" }) })] })) }, addr.id || index));
                        })) : (_jsx("p", { children: "No addresses found." })), addresses.length < 2 && canAddNewAddress && !showNewAddressForm && (_jsx("button", { onClick: () => setShowNewAddressForm(true), className: "add-address-button", children: missingAddressType === "billing"
                                ? "Add Billing Address"
                                : "Add Shipping Address" })), showNewAddressForm && (_jsxs(_Fragment, { children: [_jsx("h4", { className: "address-subtitle", children: missingAddressType === "billing"
                                        ? "Adding Billing Address"
                                        : "Adding Shipping Address" }), _jsxs("div", { className: "address-edit-form", children: [_jsx("input", { type: "text", value: newAddress.streetName, onChange: (e) => setNewAddress(Object.assign(Object.assign({}, newAddress), { streetName: e.target.value })), placeholder: "Street Name", className: "edit-input" }), newAddressErrors.streetName && (_jsx("p", { className: "error-message", children: newAddressErrors.streetName })), _jsx("input", { type: "text", value: newAddress.postalCode, onChange: (e) => setNewAddress(Object.assign(Object.assign({}, newAddress), { postalCode: e.target.value })), placeholder: "Postal Code", className: "edit-input" }), newAddressErrors.postalCode && (_jsx("p", { className: "error-message", children: newAddressErrors.postalCode })), _jsx("input", { type: "text", value: newAddress.city, onChange: (e) => setNewAddress(Object.assign(Object.assign({}, newAddress), { city: e.target.value })), placeholder: "City", className: "edit-input" }), newAddressErrors.city && (_jsx("p", { className: "error-message", children: newAddressErrors.city })), _jsxs("select", { value: newAddress.country, onChange: (e) => setNewAddress(Object.assign(Object.assign({}, newAddress), { country: e.target.value })), className: "edit-input", children: [_jsx("option", { value: "", children: "Select a country" }), europeanCountries.map(({ code, name }) => (_jsx("option", { value: code, children: name }, code)))] }), newAddressErrors.country && (_jsx("p", { className: "error-message", children: newAddressErrors.country })), _jsxs("div", { className: "edit-button-row", children: [_jsx("button", { onClick: handleAddNewAddress, className: `save-button ${isNewAddressSaveDisabled ? "disabled" : ""}`, disabled: isNewAddressSaveDisabled, children: "Save New Address" }), _jsx("button", { onClick: () => setShowNewAddressForm(false), className: "close-button", children: "Cancel" })] })] })] }))] })] }) }));
};
export default ProfilePage;
